import { APP_CUSTOM_SCHEME } from "@src/mobile/utils/deepLinkHandler";
import { Platform } from "@src/mobile/utils/platform";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// eslint-disable-next-line no-restricted-imports -- Dispatching Cognito session thunk in auth redirect handler
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { MobileAuthLoading } from "../../../mobile/Login/MobileAuthLoading";
import { FirebaseAuth } from "../../../utils/firebase";
import { APP_V2_APP_EVENTS, logError, logEvent } from "../../lib/analytics";
import { showErrorToast } from "../../lib/Notifications";
import { completeMagicLinkSignIn, getExistingSessionEmail } from "../cognito/cognitoAuth";
import { exchangeCognitoTokenForFirebase } from "../cognito/exchangeCognitoTokenForFirebase";
import { isFirebaseSessionFallbackEnabled } from "../cognito/firebaseSessionFallbackState";
import { initCognitoSession } from "../cognito/initCognitoSession";
import { isSafeRedirectUrl } from "../cognito/isSafeRedirectUrl";
import { mapCognitoError } from "../cognito/mapCognitoError";
import { AuthRedirectView } from "./AuthRedirectView";
import { EMAIL_LOGIN_LINK_PATH } from "./paths";

const DEFAULT_REDIRECT_PATH = "/";

type AuthState =
  | "checking-session"
  | "already-authenticated"
  | "confirm"
  | "signing-in"
  | "redirecting"
  | "error"
  | "invalid";

export function AuthRedirect() {
  const location = useLocation();
  const history = useHistory();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();
  const [authState, setAuthState] = useState<AuthState>("checking-session");
  const [error, setError] = useState<string | undefined>();
  const [existingEmail, setExistingEmail] = useState<string | undefined>();
  const hasAttemptedAuth = useRef(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const payload = searchParams.get("payload");
  const email = searchParams.get("email");

  // Remove sensitive token from the URL immediately on mount.
  useEffect(() => {
    globalThis.history.replaceState(null, "", globalThis.location.pathname);
  }, []);

  const handleGoToDashboard = useCallback(() => {
    const requestedUrl = searchParams.get("requestedUrl");
    const target =
      requestedUrl && isSafeRedirectUrl(requestedUrl) ? requestedUrl : DEFAULT_REDIRECT_PATH;
    history.replace(target);
  }, [history, searchParams]);

  const handleContinue = useCallback(async () => {
    if (hasAttemptedAuth.current) {
      return;
    }
    hasAttemptedAuth.current = true;

    setAuthState("signing-in");

    try {
      const requestedUrl = searchParams.get("requestedUrl");

      logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_STARTED);

      if (!payload || !email) {
        const detail = !payload ? "payload" : "email";
        logError(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_FAILURE, {
          error: new Error(`Missing ${detail} in magic link`),
          metadata: {
            errorCode: "auth/invalid-link",
            errorMessage: `Missing ${detail}`,
          },
        });

        setError("This login link is invalid or incomplete. Please request a new login link.");
        setAuthState("error");
        return;
      }

      const cognitoCredentials = await completeMagicLinkSignIn({
        payload,
        email,
      });

      logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_COGNITO_SUCCESS);

      if (isFirebaseSessionFallbackEnabled()) {
        try {
          const firebaseCustomToken = await exchangeCognitoTokenForFirebase(
            cognitoCredentials.idToken
          );
          await FirebaseAuth.signInWithCustomToken(firebaseCustomToken);
        } catch (firebaseExchangeError: unknown) {
          // Non-fatal: the Cognito session is already valid. This fails for
          // SSO-linked employees whose tokens include a Rippling identity
          // that the exchange endpoint doesn't handle. Log and continue —
          // initCognitoSession below will hydrate the session from Cognito.
          logError(APP_V2_APP_EVENTS.FIREBASE_FALLBACK_COGNITO_EXCHANGE_FAILURE, {
            error: firebaseExchangeError,
            metadata: { stage: "firebase-exchange-fallback" },
          });
        }
      }

      logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_SUCCESS);

      setAuthState("redirecting");

      if (email) {
        localStorage.setItem("emailForSignIn", email);
      }

      const target =
        requestedUrl && isSafeRedirectUrl(requestedUrl) ? requestedUrl : DEFAULT_REDIRECT_PATH;

      // Hydrate the Redux session before navigating so PrivateRoute sees a
      // valid session immediately. Without this in the Firebase fallback path,
      // there is a race where onIdTokenChanged hasn't fired yet, Redux has no
      // session, and PrivateRoute briefly flashes the login page.
      await dispatch(initCognitoSession());

      if (isFirebaseSessionFallbackEnabled() && target !== DEFAULT_REDIRECT_PATH) {
        localStorage.setItem("redirectURL", target);
      }

      history.replace(target);
    } catch (authError: unknown) {
      hasAttemptedAuth.current = false;
      const catchEmail = searchParams.get("email") ?? undefined;
      const mappedError = mapCognitoError(authError);
      // Show a link-expired message for generic/unknown errors (e.g. expired or invalid link).
      // For auth/user-not-found, show the actual "user not found" message so the user knows
      // there is no account for their email address.
      const errorMessage =
        mappedError.code === "auth/unknown"
          ? "Link verification failed. The link may have expired. Please request a new login link."
          : mappedError.message;

      logError(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_FAILURE, {
        error: authError,
        metadata: {
          errorCode: mappedError.code,
          errorMessage: mappedError.message,
          email: catchEmail,
        },
      });
      if (mappedError.code === "auth/user-type-not-allowed") {
        logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_USER_TYPE_NOT_ALLOWED, {
          email: catchEmail,
        });
      }

      setError(errorMessage);
      setAuthState("error");
      showErrorToast(errorMessage);
    }
  }, [payload, email, searchParams, history, dispatch]);

  const handleSignOutAndContinue = useCallback(async () => {
    try {
      setAuthState("signing-in");
      // Do NOT sign out of Cognito or Firebase here:
      // - Cognito: completeMagicLinkSignIn → initiateCustomAuth handles
      //   UserAlreadyAuthenticatedException internally (signOut → retry).
      //   Calling cognitoSignOut() separately can leave Amplify in a state
      //   where the new session tokens aren't persisted to localStorage.
      // - Firebase: signOut() triggers onIdTokenChanged → Redux LOGOUT →
      //   component remount. signInWithCustomToken replaces the session.
      setExistingEmail(undefined);
      hasAttemptedAuth.current = false;
      await handleContinue();
    } catch (error: unknown) {
      const message = "Unable to sign in. Please try again.";
      logError(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_FAILURE, {
        error,
        metadata: { stage: "sign-out-and-continue" },
      });
      setError(message);
      setAuthState("error");
      showErrorToast(message);
    }
  }, [handleContinue]);

  // Check for existing session on mount
  useEffect(() => {
    if (!payload || !email) {
      setAuthState("invalid");
      return;
    }

    let cancelled = false;
    const SESSION_CHECK_TIMEOUT_MS = 20_000;

    void (async () => {
      const sessionEmail = await Promise.race<string | undefined>([
        getExistingSessionEmail(),
        new Promise<undefined>((resolve) =>
          setTimeout(() => resolve(undefined), SESSION_CHECK_TIMEOUT_MS)
        ),
      ]);

      if (cancelled) {
        return;
      }

      if (sessionEmail) {
        setExistingEmail(sessionEmail);
        setAuthState("already-authenticated");
      } else if (Platform.isMobile) {
        // In the native app the user already confirmed intent by tapping "Open"
        // — skip the confirm screen and authenticate immediately.
        void handleContinue();
      } else {
        setAuthState("confirm");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [payload, email, handleContinue]);

  // When Universal Links don't fire (e.g. Gmail/Outlook embedded webviews),
  // build a custom-scheme URL so the user can tap "Open in App" manually.
  const appSchemeUrl = useMemo(() => {
    if (!Platform.isMobileBrowser || !payload || !email) {
      return undefined;
    }
    const appParams = new URLSearchParams({ payload, email });
    const requestedUrl = searchParams.get("requestedUrl");
    if (requestedUrl) {
      appParams.set("requestedUrl", requestedUrl);
    }
    return `${APP_CUSTOM_SCHEME}://${EMAIL_LOGIN_LINK_PATH}?${appParams.toString()}`;
  }, [payload, email, searchParams]);

  if (Platform.isMobile) {
    return (
      <MobileAuthLoading
        error={authState === "error" ? (error ?? "Sign in failed. Please try again.") : undefined}
        onRetry={() => {
          history.replace("/login");
        }}
      />
    );
  }

  return (
    <AuthRedirectView
      authState={authState}
      error={error}
      existingEmail={existingEmail}
      magicLinkEmail={email}
      appSchemeUrl={appSchemeUrl}
      onContinue={() => {
        void handleContinue();
      }}
      onSignOutAndContinue={() => {
        void handleSignOutAndContinue();
      }}
      onGoToDashboard={handleGoToDashboard}
    />
  );
}
